import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CancelButtonLabel,
  ContentContainer,
  Divider,
  ErrorMessage,
  ModalContainer,
  RootContainer,
  SuccessMessageDescription,
  SuccessMessageTitle,
  Title,
  UploadButton,
  UploadButtonLabel,
  UploadButtonsContainer,
} from "@components/UploadTrainingModalCst/style";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckIcon from "@mui/icons-material/Check";
import { downloadFile, ServicesURL } from "@utils/utilsApi";
import { getCookie } from "@utils/utilsCookie";
import { axiosInstance } from "../../services/config";
import { useParams } from "react-router-dom";

interface UploadTrainingModalCstProps {
  isOpen: boolean;
  onClose: () => void;
}

const sessionId = getCookie("sessionId");
const fileMaxSize = 15000000;
const fileMinSize = 10;

const UploadTrainingModalCst = ({
  isOpen,
  onClose,
}: UploadTrainingModalCstProps) => {
  const { t } = useTranslation(["skillBook-page"]);
  const theme = useTheme();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { id } = useParams();

  useEffect(() => {
    setShowSuccessMessage(false);
    setSelectedFile(undefined);
    setErrorMessage("");
  }, [isOpen]);

  const onUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      if (!["text/csv"].includes(file.type)) {
        setErrorMessage(
          t("validation.file_type_error", { acceptedFormats: "CSV" })!,
        );
      } else if (file.size < fileMinSize || file.size > fileMaxSize) {
        setErrorMessage(
          t("validation.file_size_error_range", {
            minSize: `${fileMinSize} byte`,
            maxSize: `${(fileMaxSize / 1000000).toFixed(1)} MB`,
          })!,
        );
      } else {
        setErrorMessage("");
        setSelectedFile(file);
      }
    }
  };

  const onConfirm = () => {
    if (!selectedFile || !id) return;

    setIsLoading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    axiosInstance
      .post(
        `${ServicesURL.getSkillBook}/bulk-upload?corporateId=${id}`,
        formData,
        {
          headers: {
            "x-ada-session-token": sessionId,
            accept: "text/csv",
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      )
      .then((res) => {
        if (res.status !== 204) {
          setErrorMessage(t("upload_error")!);

          const href = URL.createObjectURL(res.data);
          const fileName = res.headers["content-disposition"]
            .split("filename=")[1]
            .split(".")[0];
          const fileExtension = res.headers["content-disposition"]
            .split(".")[1]
            .split(";")[0];

          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", `${fileName}.${fileExtension}`); //or any other extension
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        } else {
          setSelectedFile(undefined);
          setShowSuccessMessage(true);
          setErrorMessage("");
        }
      })
      .catch((error) => {
        console.error("fileUpload", error);

        if (error?.code === "ERR_NETWORK") {
          setErrorMessage(t("completed_upload_with_error")!);
        } else {
          setErrorMessage(t("upload_error_generic")!);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onDownloadTemplate = async () => {
    await downloadFile(
      `${ServicesURL.getSkillBook}/${id}/bulk-upload/template`,
      false,
      "",
      {
        headers: {
          "x-ada-session-token": sessionId,
        },
      },
    );
  };

  const SuccessMessage = (
    <Stack direction={"column"} gap={5}>
      <SuccessMessageTitle>
        {t("upload-training-modal.success-title")}
      </SuccessMessageTitle>
      <Stack
        direction="row"
        spacing={3}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <CheckIcon color={"success"} />
        <SuccessMessageDescription>
          {t("upload-training-modal.success-message")}
        </SuccessMessageDescription>
      </Stack>
      <Button
        variant={"contained"}
        onClick={onClose}
        sx={{
          alignSelf: "center",
        }}
      >
        {t("finish")}
      </Button>
    </Stack>
  );

  return (
    <Modal open={isOpen} onClose={onClose}>
      <RootContainer>
        <ModalContainer theme={theme}>
          {showSuccessMessage ? (
            SuccessMessage
          ) : (
            <>
              <Title variant={"h4"}>{t("upload-training-modal.title")}</Title>

              <Divider />

              <ContentContainer>
                <Typography
                  sx={{
                    fontsize: 16,
                  }}
                >
                  {t("upload-training-modal.description")}
                </Typography>
                <UploadButtonsContainer>
                  <input
                    type={"file"}
                    accept={".csv,.CSV"}
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    endIcon={<FileDownloadIcon />}
                    variant={"outlined"}
                    onClick={onDownloadTemplate}
                    disabled={isLoading}
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("upload-training-modal.download-template-cta")}
                  </Button>
                  <UploadButton
                    endIcon={
                      <FileUploadIcon
                        sx={{ color: theme.palette.text.primary }}
                      />
                    }
                    variant={"text"}
                    onClick={onUploadClick}
                    disabled={isLoading}
                  >
                    <UploadButtonLabel>
                      {selectedFile?.name ??
                        t("upload-training-modal.upload-cta")}
                    </UploadButtonLabel>
                  </UploadButton>
                </UploadButtonsContainer>
              </ContentContainer>

              {errorMessage && (
                <ErrorMessage
                  dangerouslySetInnerHTML={{ __html: errorMessage }}
                />
              )}

              <Divider />

              <Stack direction={"row"} spacing={2}>
                <Button variant={"text"} onClick={onClose}>
                  <CancelButtonLabel>
                    {t("upload-training-modal.cancel-cta")}
                  </CancelButtonLabel>
                </Button>
                <Button
                  disabled={!selectedFile || isLoading}
                  variant={"contained"}
                  onClick={onConfirm}
                >
                  {t("upload-training-modal.confirm-cta")}
                </Button>
              </Stack>
            </>
          )}
        </ModalContainer>
      </RootContainer>
    </Modal>
  );
};

export default UploadTrainingModalCst;
